/**
 * WidgetCard — shared shell for every dashboard widget (and any other
 * section that wants a titled card surface). Provides: title row,
 * optional right-side header slot, consistent surface (white bg, rounded,
 * shadow, padding), loading + error states, grid sizing.
 *
 * Reusable outside the dashboard: any titled panel can use this.
 */

import * as React from "react";

export interface WidgetCardProps {
  title: string;
  /** column span in the parent grid; defaults to 1 */
  w?: number;
  /** row span; defaults to 1 */
  h?: number;
  /**
   * 0-indexed column for explicit placement at lg+. When omitted, the card
   * relies on CSS auto-flow for positioning (mobile/tablet always do).
   * Requires the parent grid to have the `dashboard-grid` class and
   * Dashboard.css imported.
   */
  x?: number;
  /** 0-indexed row for explicit placement at lg+. See `x`. */
  y?: number;
  /**
   * Optional icon shown to the LEFT of the title (e.g. <ChartBarIcon /> for
   * data/chart widgets per Penpot). Pure visual; sits inline with the title
   * and inherits the same row height.
   */
  icon?: React.ReactNode;
  /** Optional header right-side slot (e.g. filter pill, time selector). */
  headerRight?: React.ReactNode;
  /** When true, body shows a skeleton; children are ignored. */
  loading?: boolean;
  /** When set, body shows error message; children are ignored. */
  error?: string | null;
  /** Optional empty-state message when not loading + no error + no children. */
  emptyMessage?: string;
  /**
   * Optional footer slot. Renders OUTSIDE the scrollable body, pinned to
   * the bottom of the card. Use for "View all" links etc. that should
   * always be visible regardless of body scroll position.
   */
  footer?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const colSpanClass: Record<number, string> = {
  1: "col-span-1 sm:col-span-2 lg:col-span-1",
  2: "col-span-1 sm:col-span-2 lg:col-span-2",
  3: "col-span-1 sm:col-span-4 lg:col-span-3",
  4: "col-span-1 sm:col-span-4 lg:col-span-4",
};

const rowSpanClass: Record<number, string> = {
  1: "row-span-1",
  2: "row-span-2",
  3: "row-span-3",
  4: "row-span-4",
};

export function WidgetCard({
  title,
  w = 1,
  h = 1,
  x,
  y,
  icon,
  headerRight,
  loading,
  error,
  emptyMessage,
  footer,
  children,
  className = "",
}: WidgetCardProps) {
  const widthClass = colSpanClass[Math.min(4, Math.max(1, w))];
  const heightClass = rowSpanClass[Math.min(4, Math.max(1, h))];

  let body: React.ReactNode;
  if (loading) body = <SkeletonBody />;
  else if (error) body = <ErrorBody message={error} />;
  else if (!children && emptyMessage) body = <EmptyBody message={emptyMessage} />;
  else body = children;

  // CSS custom properties for explicit placement at lg+ (gated by .dashboard-widget
  // in Dashboard.css). React.CSSProperties doesn't type custom vars, so cast.
  const styleVars: Record<string, string | number> = {};
  if (typeof x === "number") styleVars["--widget-col-start"] = x + 1;
  if (typeof y === "number") styleVars["--widget-row-start"] = y + 1;

  return (
    <section
      className={`dashboard-widget ${widthClass} ${heightClass} bg-white rounded-lg border border-gray-200 p-5 flex flex-col min-h-[140px] overflow-hidden ${className}`}
      style={styleVars as React.CSSProperties}
    >
      <header className="flex items-center justify-between gap-2 mb-4 flex-shrink-0">
        {/* Penpot title: Inter 14 / 600, gray-600, uppercase, slight tracking.
            truncate + min-w-0 stops the title from forcing a header line wrap
            when narrow widgets carry wide headerRight controls (toggle pills). */}
        <div className="flex items-center gap-1.5 min-w-0">
          {icon && <span className="flex-shrink-0 inline-flex">{icon}</span>}
          <h3 className="text-[14px] font-semibold tracking-wider text-gray-600 uppercase truncate min-w-0">
            {title}
          </h3>
        </div>
        {headerRight && <div className="flex-shrink-0">{headerRight}</div>}
      </header>
      {/* Body scrolls internally so a tall widget can't stretch its grid row. */}
      <div className="flex-1 min-h-0 overflow-y-auto">{body}</div>
      {/* Footer is pinned outside the scroll area — always visible at card bottom. */}
      {footer && !loading && !error && (
        <div className="flex-shrink-0 mt-3 pt-3 border-t border-gray-200">{footer}</div>
      )}
    </section>
  );
}

function SkeletonBody() {
  return (
    <div className="space-y-2 animate-pulse" aria-label="loading">
      <div className="h-3 w-3/4 bg-gray-200 rounded" />
      <div className="h-3 w-1/2 bg-gray-200 rounded" />
      <div className="h-3 w-5/6 bg-gray-200 rounded" />
    </div>
  );
}

function ErrorBody({ message }: { message: string }) {
  return <div className="text-sm text-red-600">{message}</div>;
}

function EmptyBody({ message }: { message: string }) {
  return <div className="text-xs text-gray-400">{message}</div>;
}
